import { Alert, Button, EmptyState, FieldBlock, Input, Select, Textarea, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { CanaisDeAcesso } from '@orbind/core'
import { temAlgumCanal } from '@orbind/core'
import { CamposDeCanais } from '../../components/CamposDeCanais.tsx'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { canalAtual } from '../../lib/canal.ts'
import { api, apiBase, webTokenStorage } from '../../lib/api.ts'
import { useCurrentWorkspace, workspaceLogoUrl } from './WorkspaceBar.tsx'
import './workspace.css'

/**
 * Edicao do workspace (SPEC-20260814-0030), no padrao FormScreen (referencia
 * Conta Azul, escolha do usuario): secoes empilhadas recolhiveis, campos em
 * colunas na linha e barra de acoes no rodape com dirty check e Descartar.
 *
 * Identidade (logo, titulo, descricao), tipo (pessoal/empresa), dados da
 * empresa recolhidos atras do nome fantasia, consulta de CNPJ na Receita
 * (BrasilAPI, via servidor), acesso por dominio e plano contratado. Qualquer
 * membro VE; so o dono grava — a API devolve 403 e a tela ja desabilita tudo
 * para deixar isso claro antes do erro.
 */

type Detalhe = {
  id: string
  name: string
  slug: string
  description: string | null
  /** Dominio de e-mail para restringir o acesso ao workspace (fica aqui — e
   *  controle de acesso do workspace; tipo/empresa/plano vivem na Conta). */
  domain: string | null
  domainRestricted: boolean
  /** Por onde o workspace é usado, por padrão (SPEC-20261008-1104-canais-de-acesso). */
  accessChannels: CanaisDeAcesso
  hasLogo: boolean
  logoVersion: string
  role: string
  /** Dono do workspace, dono da conta ou super admin (SPEC-20261008-1233-editar-workspace-dono-da-conta). */
  podeEditar?: boolean
}

export function WorkspaceSettingsPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const atual = useCurrentWorkspace()
  const fileRef = useRef<HTMLInputElement | null>(null)

  const detalhe = useQuery({
    queryKey: ['workspace-detail', atual?.id],
    enabled: atual !== null,
    queryFn: async () => {
      const resposta = await api.workspaces[':id'].$get({ param: { id: atual!.id } })
      if (!resposta.ok) throw new Error('Não foi possível carregar o workspace.')
      return ((await resposta.json()) as { workspace: Detalhe }).workspace
    },
  })

  // formulario espelha o detalhe quando ele chega
  const [form, setForm] = useState<Detalhe | null>(null)
  useEffect(() => {
    if (detalhe.data) setForm(detalhe.data)
  }, [detalhe.data])

  // quem edita: dono do workspace, dono da conta ou super admin — o servidor diz
  const dono = detalhe.data?.podeEditar ?? detalhe.data?.role === 'owner'
  const muda = <K extends keyof Detalhe>(campo: K, valor: Detalhe[K]) =>
    setForm((f) => (f ? { ...f, [campo]: valor } : f))

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['workspaces'] })
    void qc.invalidateQueries({ queryKey: ['workspace-detail'] })
  }

  const salvar = useMutation({
    mutationFn: async () => {
      const f = form!
      const resposta = await api.workspaces[':id'].$patch({
        param: { id: f.id },
        json: {
          name: f.name.trim(),
          description: f.description?.trim() || null,
          domain: f.domain?.trim().toLowerCase() || null,
          domainRestricted: f.domain ? f.domainRestricted : false,
          accessChannels: f.accessChannels,
        },
      })
      const corpo = (await resposta.json()) as { workspace?: unknown; error?: { message?: string } }
      if (!resposta.ok) throw new Error(corpo.error?.message ?? 'Não foi possível salvar.')
    },
    onSuccess: () => {
      recarregar()
      toast.push({ tone: 'success', message: 'Workspace atualizado.' })
    },
    onError: (erro) =>
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' }),
  })

  /**
   * So os campos que o botao Salvar persiste, normalizados como o PATCH
   * enviaria — e o que decide se ha alteracao pendente (botao habilitado).
   */
  const persistiveis = (d: Detalhe) => ({
    name: d.name.trim(),
    description: d.description?.trim() || null,
    domain: d.domain?.trim().toLowerCase() || null,
    domainRestricted: d.domain ? d.domainRestricted : false,
    accessChannels: d.accessChannels,
  })
  const alterado =
    form !== null && detalhe.data !== undefined &&
    JSON.stringify(persistiveis(form)) !== JSON.stringify(persistiveis(detalhe.data))

  // Exclusao do workspace (SPEC-20260814-2128): dono, senha + EXCLUIR.
  const [senhaExcluirWs, setSenhaExcluirWs] = useState('')
  const [confirmaExcluirWs, setConfirmaExcluirWs] = useState('')
  const [erroExcluirWs, setErroExcluirWs] = useState<string | null>(null)

  const excluirWs = useMutation({
    mutationFn: async () =>
      (await api.workspaces[':id'].$delete({ param: { id: form!.id }, json: { password: senhaExcluirWs } })).json(),
    onSuccess: (dados) => {
      if (dados.needsWorkspace || !dados.token) {
        // ficou sem nenhum workspace: sessao morre e a tela de criacao assume
        webTokenStorage.set(null)
        window.location.assign('/login')
        return
      }
      // troca a sessao para o proximo workspace e recarrega o app inteiro
      webTokenStorage.set(dados.token)
      window.location.assign('/home')
    },
    onError: (erro) =>
      setErroExcluirWs(erro instanceof Error && erro.message ? erro.message : 'Não foi possível excluir o workspace.'),
  })

  const enviarLogo = async (arquivo: File | null) => {
    if (!arquivo || !form) return
    const corpo = new FormData()
    corpo.append('file', arquivo)
    const resposta = await fetch(`${apiBase()}/workspaces/${form.id}/logo`, {
      method: 'POST',
      headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
      body: corpo,
    })
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível enviar o logo.' })
      return
    }
    recarregar()
    toast.push({ tone: 'success', message: 'Logo atualizado.' })
  }

  const removerLogo = async () => {
    if (!form) return
    const resposta = await api.workspaces[':id'].logo.$delete({ param: { id: form.id } })
    if (!resposta.ok) {
      toast.push({ tone: 'danger', message: 'Não foi possível remover o logo.' })
      return
    }
    recarregar()
    toast.push({ tone: 'success', message: 'Logo removido.' })
  }

  if (detalhe.isLoading || (detalhe.data && !form)) {
    return (
      <div className="wsset">
        <div className="wsset__card" aria-busy>
          <div className="wsset__skeleton" />
          <div className="wsset__skeleton" style={{ width: '60%' }} />
          <div className="wsset__skeleton" style={{ width: '80%' }} />
        </div>
      </div>
    )
  }

  if (detalhe.isError || !form || !atual) {
    return (
      <div className="wsset">
        <EmptyState
          icon="fas fa-briefcase"
          title="Não foi possível carregar o workspace"
          description="Verifique sua conexão e tente novamente."
          action={
            <Button variant="primary" onClick={() => void detalhe.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      </div>
    )
  }

  const salvarValido =
    dono && alterado && form.name.trim().length >= 2 && temAlgumCanal(form.accessChannels) && !salvar.isPending
  // quem está salvando fecharia o próprio canal (o colaborador com regra
  // própria não é afetado, mas o aviso é para quem edita agora)
  const fechaOProprioCanal = !form.accessChannels[canalAtual()]
  const inicial = (form.name.trim() || '?').slice(0, 1).toUpperCase()

  return (
    <FormScreen
      title="Editar workspace"
      subtitle={detalhe.data ? detalhe.data.name : undefined}
      dirty={alterado}
      dirtyHint={dono ? 'Tudo salvo' : 'Somente leitura — só o dono do workspace ou da conta edita'}
      saveLabel="Salvar"
      saveDisabled={!salvarValido}
      saving={salvar.isPending}
      onSubmit={() => salvarValido && salvar.mutate()}
      footerExtra={
        <Button variant="ghost" disabled={!alterado} onClick={() => detalhe.data && setForm(detalhe.data)}>
          Descartar
        </Button>
      }
    >
      {/* -------------------------------------------------- identidade */}
      <FormSection title="Informações do workspace">
        <div className="wsset__logo-row">
          <span className="wsset__logo" aria-hidden>
            {form.hasLogo ? <img src={workspaceLogoUrl(form)} alt="" /> : inicial}
          </span>
          <div className="wsset__logo-actions">
            <Button variant="secondary" icon="far fa-image" disabled={!dono} onClick={() => fileRef.current?.click()}>
              {form.hasLogo ? 'Trocar logo' : 'Definir logo'}
            </Button>
            {form.hasLogo ? (
              <Button variant="ghost" icon="far fa-trash-alt" disabled={!dono} onClick={() => void removerLogo()}>
                Remover
              </Button>
            ) : null}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              void enviarLogo(e.target.files?.[0] ?? null)
              e.target.value = ''
            }}
          />
        </div>

        <FormRow cols="1fr 2fr">
          <FieldBlock label="Título" required>
            {({ id }) => (
              <Input id={id} value={form.name} disabled={!dono} onChange={(e) => muda('name', e.target.value)} />
            )}
          </FieldBlock>
          <FieldBlock label="Descrição">
            {({ id }) => (
              <Input
                id={id}
                placeholder="Para que serve este workspace…"
                value={form.description ?? ''}
                disabled={!dono}
                onChange={(e) => muda('description', e.target.value)}
              />
            )}
          </FieldBlock>
        </FormRow>
      </FormSection>

      {/* ------------------------------------------- dominio de acesso */}
      <FormSection title="Domínio de acesso">
        <FormRow cols="1fr 1fr">
          <FieldBlock label="Domínio da empresa" hint="Ex.: empresa.com.br — o domínio dos e-mails corporativos.">
            {({ id }) => (
              <Input
                id={id}
                placeholder="empresa.com.br"
                value={form.domain ?? ''}
                disabled={!dono}
                onChange={(e) => muda('domain', e.target.value.trim().toLowerCase() || null)}
              />
            )}
          </FieldBlock>
        </FormRow>
        <label className={`wsset__restrict${!dono ? ' wsset__restrict--off' : ''}`}>
          <input
            type="checkbox"
            checked={form.domainRestricted}
            disabled={!dono || !form.domain}
            onChange={(e) => muda('domainRestricted', e.target.checked)}
          />
          <span>
            <strong>Acesso somente pelo domínio</strong>
            <small>
              {form.domain
                ? `Apenas e-mails @${form.domain} conseguem entrar neste workspace (vale no login e na troca de workspace).`
                : 'Informe o domínio acima para poder restringir o acesso.'}
            </small>
          </span>
        </label>
        <p className="wsset__note">
          <i className="fas fa-circle-info" aria-hidden /> Tipo (pessoa física/empresa), dados da empresa e plano agora
          ficam na <strong>Conta</strong> — edite pelo menu do seu perfil.
        </p>
      </FormSection>


      {/* ------------------------------------------- canais de acesso */}
      <FormSection title="Canais de acesso">
        <p className="wsset__danger-desc">
          Por onde os colaboradores usam este workspace, por padrão. No cadastro de cada colaborador dá para definir
          uma regra própria, que vence esta. Quem entra por um canal fechado vê uma tela explicando por onde entrar —
          se o desktop estiver liberado, com o download do Orbind para instalar na máquina.
        </p>
        <CamposDeCanais
          value={form.accessChannels}
          disabled={!dono}
          onChange={(canais) => muda('accessChannels', canais)}
        />
        {dono && fechaOProprioCanal && temAlgumCanal(form.accessChannels) ? (
          <Alert tone="warning" title="Você está usando um canal que vai ser fechado">
            Depois de salvar, quem segue o padrão do workspace — inclusive você, se não tiver regra própria — não
            entra mais por aqui.
          </Alert>
        ) : null}
      </FormSection>

      {/* ------------------------------------------ zona de perigo (dono) */}
      {/* excluir continua só do DONO do workspace (a API recusa os demais) */}
      {detalhe.data?.role === 'owner' ? (
        <FormSection
          title={
            <>
              <i className="fas fa-triangle-exclamation" aria-hidden /> Excluir workspace
            </>
          }
          tone="danger"
          defaultOpen={false}
        >
          <p className="wsset__danger-desc">
            Exclusão <strong>imediata e definitiva</strong>: todos os Itens de Trabalho, demandas, quadros, cadastros
            e membros deste workspace são apagados. Se você ficar sem nenhum workspace, cairá na tela de criação de um
            novo.
          </p>
          {erroExcluirWs ? (
            <Alert tone="danger" title="Não foi possível excluir">
              {erroExcluirWs}
            </Alert>
          ) : null}
          <FormRow cols="1fr 1fr">
            <FieldBlock label="Sua senha" required>
              {({ id }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={senhaExcluirWs}
                  onChange={(e) => setSenhaExcluirWs(e.target.value)}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Digite EXCLUIR para confirmar" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={confirmaExcluirWs}
                  placeholder="EXCLUIR"
                  onChange={(e) => setConfirmaExcluirWs(e.target.value)}
                />
              )}
            </FieldBlock>
          </FormRow>
          <div>
            <Button
              variant="danger"
              icon="fas fa-trash-alt"
              loading={excluirWs.isPending}
              disabled={senhaExcluirWs.length === 0 || confirmaExcluirWs.trim().toUpperCase() !== 'EXCLUIR'}
              onClick={() => excluirWs.mutate()}
            >
              Excluir este workspace
            </Button>
          </div>
        </FormSection>
      ) : null}
    </FormScreen>
  )
}
